import { formatDate } from '@/lib/date';
import { type ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/data-table';
import { DueDateCell } from './due-date-cell.js';

// Property-address column shared between OpenReviewsTable and CompletedReviewsTable.
export function propertyAddressColumn<
  T extends { propertyAddress?: string | null; propertyId: string },
>(): ColumnDef<T> {
  return {
    accessorKey: 'propertyAddress',
    enableSorting: false,
    meta: { label: 'Property', isPrimary: true, isMandatory: true, searchable: true },
    header: () => (
      <span className="pl-6 text-xs uppercase tracking-wider font-semibold">Property</span>
    ),
    cell: ({ row }) => (
      <div className="pl-6 text-sm text-muted-foreground line-clamp-1">
        {row.original.propertyAddress ?? `Property #${row.original.propertyId}`}
      </div>
    ),
  };
}

// Visit-date column shared between OpenReviewsTable and CompletedReviewsTable.
export function visitDateColumn<T extends { visitDate: string }>(isMobile: boolean): ColumnDef<T> {
  return {
    accessorKey: 'visitDate',
    enableSorting: true,
    meta: { label: 'Visit Date' },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Visit Date" />,
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground whitespace-nowrap">
        {formatDate(
          row.original.visitDate,
          isMobile ? { month: 'short', day: 'numeric' } : { dateStyle: 'medium' },
        )}
      </span>
    ),
  };
}

// Due-date column with overdue indicator; wraps DueDateCell.
export function dueDateColumn<T>(
  accessorFn: (row: T) => string | undefined,
  isMobile: boolean,
  opts?: { id?: string; label?: string; title?: string; className?: string },
): ColumnDef<T> {
  return {
    id: opts?.id ?? 'dueDate',
    accessorFn,
    enableSorting: true,
    meta: {
      label: opts?.label ?? 'Due Date',
      ...(opts?.className && { className: opts.className }),
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={opts?.title ?? 'Due'} />,
    cell: ({ row }) => <DueDateCell dueDate={accessorFn(row.original)} compact={isMobile} />,
  };
}

// Right-aligned sortable timestamp column; hidden by default (for supplemental info like "Added").
export function createdAtColumn<T>(
  accessorFn: (row: T) => string,
  opts?: { id?: string; label?: string; title?: string },
): ColumnDef<T> {
  return {
    id: opts?.id ?? 'createdAt',
    accessorFn,
    enableSorting: true,
    meta: { label: opts?.label ?? 'Added', defaultHidden: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={opts?.title ?? 'Added'} />
    ),
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{accessorFn(row.original)}</span>
    ),
  };
}
